import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TourContext = createContext(null);

export function TourProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [tours, setTours] = useState([]);
  const [currentTour, setCurrentTour] = useState(null);
  const [overlayButtons, setOverlayButtons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch tours when authenticated
  const fetchTours = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tours`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setTours(data);
        // Do NOT auto-select a tour - user must choose
        // This prevents confusion about which model is loaded
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTours();
    } else {
      // Clear tours when logged out
      setTours([]);
      setCurrentTour(null);
      setOverlayButtons([]);
    }
  }, [isAuthenticated, fetchTours]);

  // Load overlay buttons when tour changes
  useEffect(() => {
    if (currentTour?.overlay_buttons) {
      setOverlayButtons(currentTour.overlay_buttons);
    } else {
      setOverlayButtons([]);
    }
  }, [currentTour]);

  const createTour = useCallback(async (tourData) => {
    try {
      const res = await fetch(`${API_URL}/api/tours`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(tourData)
      });

      if (res.ok) {
        const newTour = await res.json();
        setTours(prev => [...prev, newTour]);
        setCurrentTour(newTour);
        return { success: true, tour: newTour };
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create tour');
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [token]);

  const updateTour = useCallback(async (tourId, tourData) => {
    try {
      const res = await fetch(`${API_URL}/api/tours/${tourId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(tourData)
      });

      if (res.ok) {
        const updatedTour = await res.json();
        setTours(prev => prev.map(t => t.id === tourId ? updatedTour : t));
        if (currentTour?.id === tourId) {
          setCurrentTour(updatedTour);
        }
        return { success: true, tour: updatedTour };
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update tour');
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [token, currentTour]);

  const deleteTour = useCallback(async (tourId) => {
    try {
      const res = await fetch(`${API_URL}/api/tours/${tourId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        // Remove from local state immediately
        setTours(prev => prev.filter(t => t.id !== tourId));
        if (currentTour?.id === tourId) {
          setCurrentTour(null);
        }
        // Also refresh from server to ensure consistency
        await fetchTours();
        return { success: true };
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete tour');
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [token, currentTour, fetchTours]);

  const selectTour = useCallback((tour) => {
    console.log('Selecting tour:', tour.name, 'Model ID:', tour.matterport_model_id);
    setCurrentTour(tour);
  }, []);

  // Load tour by share token (public access)
  const loadSharedTour = useCallback(async (shareToken) => {
    try {
      const res = await fetch(`${API_URL}/api/tours/share/${shareToken}`);
      if (res.ok) {
        const tour = await res.json();
        setCurrentTour(tour);
        return { success: true, tour };
      } else {
        throw new Error('Tour not found or link expired');
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ============ Overlay Button Functions ============
  
  const addOverlayButton = useCallback(async (buttonData) => {
    if (!currentTour?.id || !token) return { success: false, error: 'No tour selected' };
    
    try {
      const res = await fetch(`${API_URL}/api/tours/${currentTour.id}/overlay-buttons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          label: buttonData.label,
          icon: buttonData.icon || 'pin',
          sweep_id: buttonData.sweepId,
          rotation: buttonData.rotation,
          position: buttonData.position,
          style: buttonData.style || 'primary'
        })
      });
      
      if (res.ok) {
        const newButton = await res.json();
        // Convert API format to frontend format
        const frontendButton = {
          id: newButton.id,
          label: newButton.label,
          icon: newButton.icon,
          sweepId: newButton.sweep_id,
          rotation: newButton.rotation,
          position: newButton.position,
          style: newButton.style
        };
        setOverlayButtons(prev => [...prev, frontendButton]);
        return { success: true, button: frontendButton };
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to add button');
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [currentTour, token]);

  const updateOverlayButton = useCallback(async (buttonId, updates) => {
    if (!currentTour?.id || !token) return { success: false, error: 'No tour selected' };
    
    try {
      const res = await fetch(`${API_URL}/api/tours/${currentTour.id}/overlay-buttons/${buttonId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          label: updates.label,
          icon: updates.icon,
          sweep_id: updates.sweepId,
          rotation: updates.rotation,
          position: updates.position,
          style: updates.style
        })
      });
      
      if (res.ok) {
        setOverlayButtons(prev => prev.map(btn => 
          btn.id === buttonId ? { ...btn, ...updates } : btn
        ));
        return { success: true };
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update button');
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [currentTour, token]);

  const removeOverlayButton = useCallback(async (buttonId) => {
    if (!currentTour?.id || !token) return { success: false, error: 'No tour selected' };
    
    try {
      const res = await fetch(`${API_URL}/api/tours/${currentTour.id}/overlay-buttons/${buttonId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        setOverlayButtons(prev => prev.filter(btn => btn.id !== buttonId));
        return { success: true };
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete button');
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [currentTour, token]);

  const value = {
    tours,
    currentTour,
    overlayButtons,
    loading,
    error,
    fetchTours,
    createTour,
    updateTour,
    deleteTour,
    selectTour,
    loadSharedTour,
    addOverlayButton,
    updateOverlayButton,
    removeOverlayButton
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
